import type Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import { Chart } from "@highcharts/react";
import { useState } from "react";
import { useFetch } from "../../hooks/api";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Loading from "../core/blocks/Loading";
import moment from "moment";
import { trellisPalette } from "./analytics/palettes";
import { formatCurrency, formatNumber } from "../../utils/formatNumber";
import { keepPreviousData } from "@tanstack/react-query";

interface Props {
  productIds: Array<string> | string;
  start: string;
  end: string;
  productPrice: number;
  marketPlace: string;
  totalUnits: number;
}

interface Inventory {
  date: string;
  product_quantity: number;
  our_sell_through_rate: number;
}

interface InventoryResults {
  results: Inventory[];
}

const ProductInventory = ({ productIds, start, end, productPrice, marketPlace, totalUnits }: Props) => {
  const { t } = useTranslation(["merchandising"]);
  const [showHelp, setShowHelp] = useState(false);
  const toggleHelp = () => setShowHelp(!showHelp);

  const today = moment().format("YYYY-MM-DD");
  let showMetrics = false;
  if (end === today) {
    showMetrics = true;
  }

  const filters = {
    // Check if productIds is an array or a string, if on type array, join the array to a string
    product_ids: Array.isArray(productIds) ? productIds.join(",") : productIds,
    start_date: moment(start).format("YYYY-MM-DD"),
    end_date: moment(end).format("YYYY-MM-DD"),
  };
  const { data, isLoading } = useFetch<InventoryResults>(
    ["product_inventory", productIds],
    `/api/product-inventory`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );
  if (isLoading) {
    return <Loading fullPage={false} height={"300px"} />;
  }

  // sort inventory by date ascending
  const reversedInventory = data?.results.sort((a, b) => moment(a.date).diff(moment(b.date))) ?? [];

  let inventoryValue = 0;
  let onHand = 0;
  let daysLeft = 0;
  let avgDailyQty = 0;
  let sellThroughRate = 0;

  if (reversedInventory.length > 0 && reversedInventory[reversedInventory.length - 1]?.date === today) {
    showMetrics = true;
    sellThroughRate = reversedInventory[reversedInventory.length - 1]?.our_sell_through_rate ?? 0;
    onHand = reversedInventory[reversedInventory.length - 1]?.product_quantity ?? 0;
    inventoryValue = onHand * productPrice;
    const diff = moment(end).diff(moment(start), "days");
    avgDailyQty = totalUnits / diff;
    daysLeft = onHand / avgDailyQty;
  }

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    chart: {
      type: "column",
      style: {
        fontFamily: "Poppins",
        fontSize: "15px",
        cursor: "zoom-in",
      },
      height: "280px",
    },
    tooltip: {
      valueDecimals: 2,
    },
    xAxis: {
      type: "datetime", // Set the x-axis type to datetime
    },
    legend: { enabled: false, verticalAlign: "top", margin: 0 },
    yAxis: [
      { title: { text: t(($) => $.merchandising["Inventory Quantity"]) } },
      { title: { text: t(($) => $.merchandising["Sell Through Rate"]) }, opposite: true, min: 0 },
    ],
    plotOptions: {
      column: {
        stacking: "normal",
        pointPadding: 0.05,
        groupPadding: 0.05,
        dataLabels: {
          enabled: true,
          style: {
            fontSize: "13px",
          },
        },
      },
    },
    series: [
      {
        name: t(($) => $.merchandising["Inventory Quantity"]),
        type: "column",
        data: reversedInventory.map((i) => [moment(i.date).valueOf(), i.product_quantity]), // Quantity data for series
        color: trellisPalette[2],
        yAxis: 0,
      },
      {
        name: t(($) => $.merchandising["Sell Through Rate"]),
        type: "line",
        data: reversedInventory.map((i) => [moment(i.date).valueOf(), i.our_sell_through_rate]), // Sell through rate data for series
        color: trellisPalette[0],
        yAxis: 1,
      },
    ],
  };

  return (
    <Row className="mt-2">
      <Col className="mb-4" style={{ display: "none " }} xs={12}>
        <div
          style={{
            padding: "2rem",
            border: "1px solid rgb(238, 238, 238)",
            borderRadius: "6px",
            backgroundColor: "rgb(248, 248, 248)",
            marginTop: "10px",
          }}
        >
          <div>
            {t(
              ($) =>
                $.merchandising[
                  "The following chart shows the inventory quantity for the selected product over the selected date range, as reported by Amazon."
                ],
            )}
            <br />
            <br />
            <span
              style={{ cursor: "pointer", color: trellisPalette[0] }}
              onClick={() => {
                toggleHelp();
              }}
            >
              {t(($) => $.merchandising["What are Sell Through Rates?"])}
            </span>
          </div>

          {showHelp && (
            <div className="pt-3">
              <p className="fs-4">
                {t(
                  ($) =>
                    $.merchandising[
                      "Sell-through rate is the ratio of units sold and shipped over a 90-day period to the average inventory available in fulfillment centers during that time. It helps measure how efficiently your products are selling and indicates whether stock levels need adjustment for optimal turnover."
                    ],
                )}
              </p>

              <p className="fs-4">{t(($) => $.merchandising["Examples:"])}</p>

              <ul className="fs-4">
                <li>
                  {t(
                    ($) =>
                      $.merchandising[
                        "A sell-through rate of 0.5 means 50% of your inventory has sold, indicating excess stock or slow sales."
                      ],
                  )}
                </li>
                <li>
                  {t(
                    ($) =>
                      $.merchandising[
                        "A sell-through rate of 1 means 100% of your inventory has sold, indicating balanced stock levels."
                      ],
                  )}
                </li>
                <li>
                  {t(
                    ($) =>
                      $.merchandising[
                        "A sell-through rate of 2 means 200% of your inventory has sold, indicating you are selling faster than replenishment, possibly leading to stock shortages."
                      ],
                  )}
                </li>
                <li>
                  {t(
                    ($) =>
                      $.merchandising[
                        "A sell-through rate of 3 means 300% of your inventory has sold, indicating high demand and potential understocking."
                      ],
                  )}
                </li>
              </ul>
            </div>
          )}
        </div>
      </Col>
      <Col xs={12}>
        {showMetrics && (
          <Row className="mb-4">
            <Col className="text-center">
              <span className="fw-bold fs-4">{t(($) => $.merchandising["Total Inventory Value"])}</span>
              <div className="fs-3">
                {formatCurrency(inventoryValue, {
                  marketPlace: marketPlace,
                })}
              </div>
            </Col>
            <Col className="text-center">
              <span className="fw-bold fs-4">{t(($) => $.merchandising["On Hand"])}</span>
              <div className="fs-3">{formatNumber(onHand, { decimalPlaces: 0 })}</div>
            </Col>
            <Col className="text-center">
              <span className="fw-bold fs-4">{t(($) => $.merchandising["Avg. Daily Qty"])}</span>
              <div className="fs-3">{formatNumber(avgDailyQty, { decimalPlaces: 0 })}</div>
            </Col>
            <Col className="text-center">
              <span className="fw-bold fs-4">{t(($) => $.merchandising["Days on Hand"])}</span>
              <div className="fs-3">{formatNumber(daysLeft, { decimalPlaces: 0 })}</div>
            </Col>
            <Col className="text-center">
              <span className="fw-bold fs-4">{t(($) => $.merchandising["Sell Through Rate"])}</span>
              <div className="fs-3">{formatNumber(sellThroughRate, { decimalPlaces: 2 })}</div>
            </Col>
          </Row>
        )}

        <Row>
          <Col xs={12} xl={12}>
            <div className="fs-4 fw-bold p-5">
              {t(($) => $.merchandising["Inventory Quantity and Sell Through Rate"])}
            </div>
            <Chart options={options} />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default ProductInventory;
